import type {
  BomCategory, BomNode, BomPreview, ImportDraftEdit, StructureImportNode, StructureImportRequest,
} from "../api/structure";

/** The server refuses more positions than this in one import (`PositionImportService.MAX_POSITIONS`). */
export const IMPORT_MAX_POSITIONS = 5000;

/**
 * One line of the previewed bill of materials as the screen holds it: what the
 * file said, plus what the user may change before importing — whether it is
 * ticked, its name and its kind.
 */
export type ImportItem = {
  /** Path in the tree ("0.3.1"); stable for the life of one preview. */
  id: string;
  parentId: string | null;
  depth: number;
  model: string;
  title: string | null;
  drawingCode: string | null;
  material: string | null;
  quantity: number;
  category: BomCategory | null;
  /** Position name; starts as the name from the drawing (columns A/B), the model name when there is none (2026-10-20). */
  name: string;
  /** Position kind code; "" until one is picked when the file's proposal has no match in the codebook. */
  kind: string;
  /** The item's own tick. It is imported only when every item above it is ticked too. */
  included: boolean;
  /** Part type the position will expect (2026-10-20); none proposed by the file. */
  expectedTypeId: string | null;
  childIds: string[];
};

export type ImportState = {
  /** Every id, parents before children, siblings in the file's order. */
  order: string[];
  items: Record<string, ImportItem>;
};

/** The preview as editable state: lines the server proposes to leave out start unticked. */
export function toImportState(preview: BomPreview): ImportState {
  const state: ImportState = { order: [], items: {} };
  const add = (node: BomNode, id: string, parentId: string | null, depth: number) => {
    const item: ImportItem = {
      id, parentId, depth,
      model: node.model, title: node.title, drawingCode: node.drawingCode, material: node.material,
      quantity: node.quantity, category: node.category,
      name: node.title?.trim() ? node.title : node.model, kind: node.kind ?? "", included: node.category === null,
      expectedTypeId: null,
      childIds: node.children.map((_, i) => `${id}.${i}`),
    };
    state.order.push(id);
    state.items[id] = item;
    node.children.forEach((child, i) => add(child, `${id}.${i}`, id, depth + 1));
  };
  preview.roots.forEach((root, i) => add(root, String(i), null, 0));
  return state;
}

/** Whether something above the item is unticked — then its own tick does not count. */
export function hasExcludedAncestor(state: ImportState, id: string): boolean {
  for (let p = state.items[id].parentId; p !== null; p = state.items[p].parentId) {
    if (!state.items[p].included) return true;
  }
  return false;
}

export function isImported(state: ImportState, id: string): boolean {
  return state.items[id].included && !hasExcludedAncestor(state, id);
}

export type ImportSummary = {
  /** Lines in the file (the whole tree). */
  items: number;
  /** Lines that will be imported. */
  selected: number;
  /** Positions that will be written: every piece is its own position, so quantities multiply down the tree. */
  positions: number;
  /** Imported lines without a kind or with an empty name — the import waits for them. */
  incomplete: number;
};

export function summarize(state: ImportState): ImportSummary {
  const out: ImportSummary = { items: state.order.length, selected: 0, positions: 0, incomplete: 0 };
  // Pieces of each imported line: its quantity times the pieces of its parent.
  const pieces: Record<string, number> = {};
  for (const id of state.order) {
    const item = state.items[id];
    const parentPieces = item.parentId === null ? 1 : pieces[item.parentId];
    if (!item.included || parentPieces === undefined) continue;
    pieces[id] = parentPieces * item.quantity;
    out.selected += 1;
    out.positions += pieces[id];
    if (!item.kind || !item.name.trim()) out.incomplete += 1;
  }
  return out;
}

function patch(state: ImportState, id: string, change: Partial<ImportItem>): ImportState {
  return { order: state.order, items: { ...state.items, [id]: { ...state.items[id], ...change } } };
}

export function setIncluded(state: ImportState, id: string, included: boolean): ImportState {
  return patch(state, id, { included });
}

export function setName(state: ImportState, id: string, name: string): ImportState {
  return patch(state, id, { name });
}

export function setKind(state: ImportState, id: string, kind: string): ImportState {
  return patch(state, id, { kind });
}

export function setExpectedType(state: ImportState, id: string, expectedTypeId: string | null): ImportState {
  return patch(state, id, { expectedTypeId });
}

/** Ticks or unticks every line of one category at once ("svi vijci"). */
export function setCategoryIncluded(state: ImportState, category: BomCategory, included: boolean): ImportState {
  const items = { ...state.items };
  for (const id of state.order) {
    if (items[id].category === category) items[id] = { ...items[id], included };
  }
  return { order: state.order, items };
}

/** How many lines of the category exist and how many of them are ticked. */
export function categoryCounts(state: ImportState, category: BomCategory): { total: number; included: number } {
  let total = 0;
  let included = 0;
  for (const id of state.order) {
    if (state.items[id].category !== category) continue;
    total += 1;
    if (state.items[id].included) included += 1;
  }
  return { total, included };
}

/** Ids to draw: everything except what sits under a collapsed line. */
export function visibleIds(state: ImportState, collapsed: ReadonlySet<string>): string[] {
  const hidden = new Set<string>();
  const out: string[] = [];
  for (const id of state.order) {
    const parentId = state.items[id].parentId;
    if (parentId !== null && (hidden.has(parentId) || collapsed.has(parentId))) {
      hidden.add(id);
      continue;
    }
    out.push(id);
  }
  return out;
}

/** Lines that start collapsed: those with something below them, from `depth` down. */
export function collapsedFrom(state: ImportState, depth: number): Set<string> {
  const out = new Set<string>();
  for (const id of state.order) {
    const item = state.items[id];
    if (item.depth >= depth && item.childIds.length > 0) out.add(id);
  }
  return out;
}

const fold = (s: string) => s.toUpperCase().replace(/[\s_]+/g, " ").trim();

/**
 * The note a position gets: the name from the drawing (unless the position already carries it
 * as its name) and the material — "NOSAČ REDUKTORA · materijal Č.0361".
 */
export function noteOf(item: Pick<ImportItem, "name" | "title" | "material">): string | null {
  const parts: string[] = [];
  if (item.title && fold(item.title) !== fold(item.name)) parts.push(item.title);
  if (item.material) parts.push(`materijal ${item.material}`);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** The ticked part of the tree, as the import request. */
export function toImportRequest(state: ImportState, targetParentId: string | null): StructureImportRequest {
  const node = (id: string): StructureImportNode => {
    const item = state.items[id];
    return {
      name: item.name.trim(),
      kind: item.kind,
      drawingCode: item.drawingCode,
      note: noteOf(item),
      quantity: item.quantity,
      ...(item.expectedTypeId ? { expectedTypeId: item.expectedTypeId } : {}),
      children: item.childIds.filter((c) => state.items[c].included).map(node),
    };
  };
  const roots = state.order.filter((id) => state.items[id].parentId === null && state.items[id].included).map(node);
  return { targetParentId, roots };
}

/**
 * The user's decisions as a draft stores them (sprint 5j §3a): per line only what differs from the
 * file's proposal (`base` = `toImportState(preview)`), so a draft stays small however big the file is.
 */
export function editsOf(state: ImportState, base: ImportState): Record<string, ImportDraftEdit> {
  const out: Record<string, ImportDraftEdit> = {};
  for (const id of state.order) {
    const now = state.items[id];
    const was = base.items[id];
    const edit: ImportDraftEdit = {};
    if (now.included !== was.included) edit.included = now.included;
    if (now.name !== was.name) edit.name = now.name;
    if (now.kind !== was.kind) edit.kind = now.kind;
    if (now.expectedTypeId !== was.expectedTypeId && now.expectedTypeId) edit.expectedTypeId = now.expectedTypeId;
    if (Object.keys(edit).length > 0) out[id] = edit;
  }
  return out;
}

/** A draft's decisions applied to the proposal; the server accepts only ids the preview has. */
export function applyEdits(base: ImportState, edits: Readonly<Record<string, ImportDraftEdit>>): ImportState {
  const items = { ...base.items };
  for (const [id, edit] of Object.entries(edits)) {
    const item = items[id];
    if (!item) continue;
    items[id] = {
      ...item,
      included: edit.included ?? item.included,
      name: edit.name ?? item.name,
      kind: edit.kind ?? item.kind,
      expectedTypeId: edit.expectedTypeId ?? item.expectedTypeId,
    };
  }
  return { order: base.order, items };
}
